<template>
  <div>
    <v-data-table
      v-model:page="page"
      :sort-by="sortBy"
      :headers="headers"
      :items="patients"
      :items-per-page="itemsPerPage"
      :items-length="sortedPatients.length"
      :loading="isLoading"
      :loading-text="t('Loading Content')"
      :no-data-text="t('No data available')"
      @update:sort-by="onUpdateSortBy"
      hide-default-footer
    >
      <template #top>
        <ConfirmationDialog
          id="deleteDialog"
          v-model="showDeleteDialog"
          :has-deletion-check="true"
          :title="
            t('Delete - Patient {patientFullName}', {
              patientFullName: selectedPatientFullName,
            })
          "
          :message="t('Are you sure you want to delete* this patient?')"
          @confirm="deleteItemConfirm"
          @cancel="closeDeleteDialog"
        />
      </template>

      <template #item.firstName="{ item }">
        {{ item.firstName }} {{ item.lastName }}
      </template>

      <template #item.sexId="{ item }">
        {{ convertFrontendSexValueToFormBuilderValue(item.sexId) }}
      </template>
      <template #item.dateOfBirth="{ item }">
        {{ formatDisplayDate(item.dateOfBirth) }}
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex flex-row align-center">
          <Button
            color="primary"
            :title="isEditable(item) ? t('Edit patient') : t('View patient')"
            :icon-btn="true"
            :icon-with-background="true"
            variant="text"
            small
            class="ma-1"
            @click="editItem(item)"
          >
            <v-icon size="small">
              {{ isEditable(item) ? 'mdi-pencil' : 'mdi-eye' }}
            </v-icon>
          </Button>
          <Button
            v-if="isDeletable(item)"
            color="primary"
            :title="t('Delete patient')"
            :icon-btn="true"
            :icon-with-background="true"
            variant="text"
            small
            class="ma-1"
            @click="deleteItem(item)"
          >
            <v-icon size="small">
              mdi-delete
            </v-icon>
          </Button>

          <Button
            v-if="showStartPatientOrder"
            color="primary"
            :title="t('Start order')"
            :icon-btn="true"
            :icon-with-background="true"
            variant="text"
            small
            class="ma-1"
            @click="startPatientOrder(item)"
          >
            <v-icon size="small">
              mdi-clipboard-plus
            </v-icon>
          </Button>
        </div>
      </template>
    </v-data-table>
    <Pagination
      v-if="pageCount > 1"
      v-model="page"
      :page-count="pageCount"
    />
  </div>
</template>
<script setup lang="ts">
import {ref, computed, onMounted} from 'vue';
import {useRouter} from 'vue-router';
import {usePatientsStore} from '@/stores/patients';
import {usePhysiciansStore} from '@/stores/physicians';
import {useOrdersStore} from '@/stores/orders';
import {usePermissionsService} from '@/composables/usePermissionsService';
import { useVue2Sort } from '@/composables/useVue2Sort'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import Pagination from '@/components/common/Pagination.vue';
import Button from '@/components/input-components/Button.vue';
import {useDateHelper, formatDisplayDate} from '@/composables/useDateHelper';
import {convertFrontendSexValueToFormBuilderValue} from '@/utils'
import {useI18n} from "vue-i18n";
import {Patient} from "@/entities/patient";

defineOptions({
  name: 'PatientsGrid'
})

const { sortDate } = useDateHelper();
const { t, locale } = useI18n()

const router = useRouter()
const patientsStore = usePatientsStore()
const physiciansStore = usePhysiciansStore()
const ordersStore = useOrdersStore()
const permissionsService = usePermissionsService()

const { sortBy, onUpdateSortBy } = useVue2Sort({ key: 'orderId', order: 'desc' })
const page = ref(1)
const itemsPerPage = 20
const isLoading = ref(false)
const showDeleteDialog = ref(false)
const selectedPatientId = ref(-1)
const selectedPatientFullName = ref('')

const headers = computed(() => [
  {
    title: t("Patient name"),
    align: 'start',
    sortable: true,
    value: 'firstName',
    width: "23%"
  },
  {
    title: t('External ID'),
    value: 'externalId',
    sortable: true,
    width: "13%"
  },
  {
    title: t('CeGaT ID'),
    value: 'patientNumber',
    sortable: true,
    width: "13%"
  },
  {
    title: t("Patient d.o.b"),
    value: 'dateOfBirth',
    sortable: true,
    sort: (a: string, b: string) => sortDate(a, b),
    width: "17%"
  },
  {
    title: t("Sex"),
    value: 'sexId',
    sortable: true,
    width: "15%"
  },
  {
    title: t("Actions"),
    value: 'actions',
    sortable: false,
    width: "9%",
  },
])

const patients = computed(() => patientsStore.filteredPatients)
const physician = computed(() => physiciansStore.getPhysician);

const pageCount = computed(() =>
  Math.ceil((sortedPatients.value?.length || 0) / itemsPerPage)
)

const isSearchingByCegatId = computed(() =>
  patientsStore.getSearchCegatId !== '' && patientsStore.getSearchCegatId !== null
)

const showStartPatientOrder = computed(() =>
  permissionsService.hasRoleExternalUser || !isSearchingByCegatId.value
)

const isEditable = (patient) => {
  return permissionsService.hasRoleExternalUser ? !patient.hasNonDraftOrders && !patient.patientId : true;
};

const isDeletable = (patient) => {
  if (permissionsService.hasRoleExternalUser) return false;
  return !patient.hasOrders;
};

const sortedPatients = computed(() => {
  const arr = [...patients.value]
  if (!sortBy.value.length) return arr

  const { key, order } = sortBy.value[0]
  const header = headers.value.find(h => h.value === key)
  const headerSort = (header as any)?.sort as ((a: any, b: any) => number) | undefined

  return arr.sort((a, b) => {
    const av = a[key]
    const bv = b[key]

    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1

    let cmp: number
    if (headerSort) {
      cmp = headerSort(av, bv)
    } else if (typeof av === 'string' && typeof bv === 'string') {
      cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' })
    } else {
      cmp = av > bv ? 1 : av < bv ? -1 : 0
    }

    return order === 'asc' ? cmp : -cmp
  })
})

async function fetchSexes() {
  isLoading.value = true;
  await patientsStore.fetchPatientsSexes();
  await fetchPatients();
  isLoading.value = false;
}

async function fetchPatients() {
  if (permissionsService.hasPatientReadPermission && !permissionsService.hasPatientReadAllPermission) {
    await patientsStore.fetchPatients();
  }
  isLoading.value = false;
}

function editItem(item: any) {
  router.push({path: `/patient/edit/${item.patientId}`});
}

function deleteItem(item: any) {
  selectedPatientId.value = item.patientId;
  selectedPatientFullName.value = `${item.firstName} ${item.lastName}`;
  showDeleteDialog.value = true;
}

async function startPatientOrder(patient: Patient) {
  patientsStore.patient = patient;
  ordersStore.setOrderPatient(patient)
  await router.push({ path: `/order/new` });
}

async function deleteItemConfirm() {
  const physId = physician.value?.userId || '';
  await patientsStore.deletePatient(selectedPatientId.value, physId);
  closeDeleteDialog();
}

function closeDeleteDialog() {
  showDeleteDialog.value = false;
  selectedPatientId.value = -1;
  selectedPatientFullName.value = '';
}

function sexNameById(sexId: number | string) {
  const sex = patientsStore.getSexById(String(sexId));
  return sex ? sex.name : '';
}

onMounted(async () => {
  patientsStore.searchInput = "";
  patientsStore.resetPatient();
  await fetchSexes()
})
</script>

